@file:OptIn(ExperimentalSerializationApi::class)

package fr.frux.shared.dto

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * Réponse de `GET /api/v1/auth/me` (#454) : le compte courant et ses droits Frux+.
 *
 * Type distinct de [UserDto] parce que [UserDto] voyage aussi dans [AuthResponse] (`verify-code`,
 * `refresh`), où l'état premium n'est pas calculé : y poser un `premium` par défaut annoncerait un
 * compte Frux+ comme inactif. Le JSON reste un sur-ensemble de [UserDto], un build publié qui lit
 * encore [UserDto] ignore le champ ajouté (`ignoreUnknownKeys`).
 *
 * [premium] nullable côté décodage : une app récente face à une API pas encore déployée ne reçoit
 * pas la clé, et doit le distinguer d'un compte sans droit (sinon un abonné Frux+ serait vu sans
 * droit, donc éligible à la publicité). Le serveur émet toujours une valeur non nulle. Les champs de
 * [PremiumStateDto] gardent `@EncodeDefault` : `encodeDefaults = false` côté serveur, un état inactif
 * doit rester explicite.
 */
@Serializable
data class CurrentUserDto(
    val id: Long? = null,
    val email: String,
    val username: String,
    @Serializable(with = InstantEpochMillisSerializer::class)
    val createdAt: Instant? = null,
    val premium: PremiumStateDto? = null,
    /**
     * Référence opaque de compte d'achat (#457, ADR 0033), à passer telle quelle au store au moment
     * de l'achat (`setObfuscatedAccountId` pour Play). Jamais l'email ni l'id. Nullable côté
     * décodage pour la même raison que [premium] : une app récente face à une API pas encore déployée.
     */
    val purchaseAccountRef: String? = null,
)

/** Corps de `POST /api/v1/premium/google` (#457) : le seul jeton, le produit vient de la réponse de Google. */
@Serializable
data class GooglePlayPurchaseRequest(
    val purchaseToken: String,
)

/**
 * État Frux+ relu en base à chaque appel, jamais dans le JWT (#454, même doctrine que `users.role`,
 * ADR 0023) : une expiration, une révocation ou un remboursement prend effet au prochain appel.
 */
@Serializable
data class PremiumStateDto(
    /** Un droit `PREMIUM` actif (non révoqué, non expiré), quelle que soit sa source. */
    @EncodeDefault
    val active: Boolean = false,
    /** Fin du droit le plus durable. `null` si inactif, ou actif sans expiration. */
    @EncodeDefault
    @Serializable(with = InstantEpochMillisSerializer::class)
    val expiresAt: Instant? = null,
    /** Source du droit retenu (`MANUAL`, `GOOGLE_PLAY`, `STRIPE`, `APP_STORE`, `REWARDED_AD`). `null` si
     *  inactif. Vocabulaire ouvert : un client ne doit pas échouer sur une valeur inconnue. */
    @EncodeDefault
    val source: String? = null,
    /** Droits à la carte actifs, hors `PREMIUM` (`AD_FREE`, `OFFLINE_ZONE`, `MATURITY_ALERT`, #527 et
     *  #531). Indépendants de [active] : un compte non abonné peut en porter. */
    @EncodeDefault
    val temporaryGrants: List<TemporaryGrantDto> = emptyList(),
)

@Serializable
data class TemporaryGrantDto(
    /** `AD_FREE`, `OFFLINE_ZONE` ou `MATURITY_ALERT`. Vocabulaire ouvert, comme [PremiumStateDto.source]. */
    val kind: String,
    /** Fin du droit. Nullable par prudence : rien n'interdit en base un droit à la carte sans fin. */
    @EncodeDefault
    @Serializable(with = InstantEpochMillisSerializer::class)
    val expiresAt: Instant? = null,
    /** Cible du droit (zone hors ligne, arbre suivi), `null` pour `AD_FREE`. */
    @EncodeDefault
    val targetRef: String? = null,
)
